import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Check, X, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import PracticeByline from "@/components/PracticeByline";
import { BANKRUPTCY_PAGES, BANKRUPTCY_SLUGS } from "@/lib/bankruptcy";
import { FIRM_PHONE, MAIN_OFFICE_PHONE, FIRM_DISCIPLINES } from "@/lib/content";
import { breadcrumbSchema, faqPageSchema, authoredPracticePageSchema } from "@/lib/schemas";
import { SITE } from "@/lib/seo";
import { BANKRUPTCY_FAQ } from "@/lib/faq";

/**
 * Builds a LegalService schema for one of the three bankruptcy pages.
 */
function serviceSchema(page) {
  return {
    "@context": "https://schema.org",
    "@type": "LegalService",
    "@id": `${SITE.url}/practice/${page.slug}#service`,
    name: page.serviceMeta.name,
    description: page.serviceMeta.seoDescription,
    serviceType: page.serviceMeta.name,
    provider: { "@id": `${SITE.url}/#organization` },
    areaServed: { "@type": "AdministrativeArea", name: "Texas" },
    url: `${SITE.url}/practice/${page.slug}`,
    audience: {
      "@type": "Audience",
      audienceType:
        page.slug === "chapter-7-individual"
          ? "Texas individuals, professionals, and business owners with personal exposure"
          : "Texas closely-held businesses and their owners",
    },
  };
}

export default function Bankruptcy() {
  const { slug } = useParams();
  const page = BANKRUPTCY_PAGES[slug];

  if (!page) return <Navigate to="/practice" replace />;

  // Pull the FAQs that match this page's slug from the central FAQ data.
  const baseFaqs = BANKRUPTCY_FAQ.filter((f) => f.slugs.includes(slug)).map(({ q, a }) => ({ q, a }));
  // Priority FAQs are page-specific curated answers (currently subchapter-v only).
  // They render first in the FAQ block AND are emitted first in FAQPage JSON-LD
  // so schema and visible content align 1:1.
  const priorityFaqs = page.priorityFaqs || [];
  const pageFaqs = [...priorityFaqs, ...baseFaqs];

  const otherPages = BANKRUPTCY_SLUGS.filter((s) => s !== slug).map((s) => BANKRUPTCY_PAGES[s]);

  return (
    <div className="bg-ivory" data-testid={`bankruptcy-page-${slug}`}>
      <SEO
        title={page.serviceMeta.seoTitle}
        description={page.serviceMeta.seoDescription}
        path={`/practice/${slug}`}
        schema={[
          ...authoredPracticePageSchema({
            path: `/practice/${slug}`,
            title: page.serviceMeta.seoTitle,
            description: page.serviceMeta.seoDescription,
            discipline: "Bankruptcy & Restructuring",
            minutes: page.readMinutes || 8,
            lastReviewed: "2026-02-01",
            publishedDate: "2024-04-01",
          }),
          serviceSchema(page),
          faqPageSchema(pageFaqs),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/practice" },
            { name: page.serviceMeta.name, path: `/practice/${slug}` },
          ]),
        ]}
      />

      {/* HERO */}
      <section className="panel-navy pt-40 pb-24 md:pt-48 md:pb-32 relative overflow-hidden" data-testid="bankruptcy-hero">
        <div className="grain grain-dark absolute inset-0 pointer-events-none" />
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 relative">
          <div className="kicker kicker-ivory">{page.kicker}</div>
          <div className="mt-3 text-[10px] uppercase tracking-[0.36em] text-brass">{FIRM_DISCIPLINES}</div>
          <div className="mt-2 text-xs uppercase tracking-[0.28em] text-ivory/60">{page.eyebrow}</div>
          <h1 className="mt-8 font-display text-6xl md:text-8xl lg:text-[8.5rem] leading-[0.92] text-ivory tracking-tight max-w-5xl">
            {page.headline}<br/>
            <span className="italic font-cormorant text-brass">{page.italic}</span>
          </h1>
          <p className="mt-12 max-w-3xl text-lg md:text-xl text-ivory/85 font-light leading-relaxed">
            {page.lede}
          </p>
          <div className="mt-12 flex flex-wrap gap-4">
            <Link to="/intake" className="btn-brass" data-testid="bankruptcy-cta-intake">
              {page.cta} <ArrowUpRight className="inline ml-2" size={18} />
            </Link>
            <a href={MAIN_OFFICE_PHONE.href} className="btn-ghost !border-ivory !text-ivory hover:!bg-ivory hover:!text-navy">
              {MAIN_OFFICE_PHONE.display}
            </a>
          </div>
        </div>
      </section>

      <PracticeByline
        minutes={page.readMinutes || 8}
        lastReviewed="February 2026"
        discipline="Bankruptcy & Restructuring"
      />

      {/* POSITION POINTS */}
      <section className="border-b-0 py-24" data-testid="bankruptcy-position">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-10 gap-y-12">
            {page.positionPoints.map((p) => (
              <div key={p.num} className="hairline-top pt-6" data-testid={`bankruptcy-point-${p.num}`}>
                <div className="font-cormorant italic text-3xl text-brass">{p.num}</div>
                <h3 className="mt-3 font-display text-2xl text-navy">{p.title}</h3>
                <p className="mt-3 text-slate2 font-light leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHEN RIGHT / WHEN NOT */}
      <section className="bg-ivory py-24 md:py-32" data-testid="bankruptcy-when">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <div className="hairline-top pt-8">
            <div className="kicker">When this is the right tool</div>
            <h2 className="mt-6 font-display text-3xl md:text-4xl text-navy leading-[1.1]">{page.whenRight.title}</h2>
            <ul className="mt-8 space-y-5" data-testid="bankruptcy-whenright">
              {page.whenRight.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span className="mt-1 shrink-0 inline-flex h-6 w-6 items-center justify-center rounded-full bg-navy text-ivory">
                    <Check size={14} strokeWidth={2.5} />
                  </span>
                  <span className="text-base md:text-lg text-ink/85 font-cormorant leading-snug">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="hairline-top pt-8">
            <div className="kicker">When it is not</div>
            <h2 className="mt-6 font-display text-3xl md:text-4xl text-navy leading-[1.1]">{page.whenNot.title}</h2>
            <ul className="mt-8 space-y-5" data-testid="bankruptcy-whennot">
              {page.whenNot.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span className="mt-1 shrink-0 inline-flex h-6 w-6 items-center justify-center rounded-full bg-oxblood text-ivory">
                    <X size={14} strokeWidth={2.5} />
                  </span>
                  <span className="text-base md:text-lg text-ink/85 font-cormorant leading-snug">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHEN A BUSINESS SHOULD EVALUATE RESTRUCTURING (optional) */}
      {page.evaluateWhen && (
        <section className="bg-linen hairline-top py-20 md:py-28" data-testid="bankruptcy-evaluate-when">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12">
            <div className="kicker">{page.evaluateWhen.kicker}</div>
            <h2 className="mt-6 font-display text-navy text-3xl md:text-5xl leading-[1.05] max-w-4xl">
              {page.evaluateWhen.heading[0]}{" "}
              <span className="italic font-cormorant text-oxblood">{page.evaluateWhen.heading[1]}</span>
            </h2>
            <p className="mt-6 max-w-3xl text-navy font-serif leading-relaxed">
              {page.evaluateWhen.lede}
            </p>
            <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12" data-testid="bankruptcy-evaluate-when-list">
              {page.evaluateWhen.signals.map((s, i) => (
                <li key={i} className="py-4 hairline-top flex gap-3" data-testid={`bankruptcy-signal-${i}`}>
                  <span className="text-brass mt-1.5 shrink-0" aria-hidden>&#9733;</span>
                  <span className="text-navy/85 font-serif leading-relaxed">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* ASSESSMENT DEPOSIT (optional) */}
      {page.assessmentDeposit && (
        <section className="bg-ivory py-20 md:py-28" data-testid="bankruptcy-assessment-deposit">
          <div className="max-w-[1200px] mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
              <div className="lg:col-span-5">
                <div className="kicker">{page.assessmentDeposit.kicker}</div>
                <h2 className="mt-6 font-display text-navy text-3xl md:text-4xl leading-[1.05]">
                  {page.assessmentDeposit.heading[0]}{" "}
                  <span className="italic font-cormorant text-oxblood">{page.assessmentDeposit.heading[1]}</span>
                </h2>
                <div className="mt-8 p-6 border-l-4 border-brass bg-linen">
                  <div className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">
                    Initial engagement
                  </div>
                  <div className="mt-3 font-display text-2xl md:text-3xl text-navy" data-testid="bankruptcy-assessment-price">
                    {page.assessmentDeposit.priceLine}
                  </div>
                </div>
              </div>
              <div className="lg:col-span-7">
                <div className="space-y-5">
                  {page.assessmentDeposit.body.map((p, i) => (
                    <p key={i} className={`${i === 0 ? "text-lg md:text-xl text-navy" : "text-navy/80"} font-serif leading-relaxed`} data-testid={`bankruptcy-assessment-body-${i}`}>
                      {p}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ASSESSMENT SCOPE (optional) */}
      {page.assessmentScope && (
        <section className="bg-linen hairline-top py-20 md:py-28" data-testid="bankruptcy-assessment-scope">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="kicker">{page.assessmentScope.kicker}</div>
            <h2 className="mt-6 font-display text-navy text-3xl md:text-5xl leading-[1.05]">
              {page.assessmentScope.heading[0]}{" "}
              <span className="italic font-cormorant text-oxblood">{page.assessmentScope.heading[1]}</span>
            </h2>
            <p className="mt-6 max-w-3xl text-navy font-serif leading-relaxed">
              {page.assessmentScope.lede}
            </p>
            <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2" data-testid="bankruptcy-assessment-scope-list">
              {page.assessmentScope.items.map(([title, desc], i) => (
                <li key={i} className="py-4 hairline-top" data-testid={`bankruptcy-scope-${i}`}>
                  <div className="flex items-baseline gap-3">
                    <span className="text-brass" aria-hidden>&#9733;</span>
                    <h3 className="font-display text-lg text-navy">{title}</h3>
                  </div>
                  <p className="mt-2 pl-6 text-sm text-navy/75 leading-relaxed font-serif">{desc}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* DECISION MATRIX */}
      <section className="bg-ivory py-24 md:py-32" data-testid="bankruptcy-matrix">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="kicker">The Decision Matrix</div>
          <h2 className="mt-6 font-display text-4xl md:text-5xl text-navy leading-[1.05] max-w-3xl">{page.matrix.title}</h2>

          <div className="mt-12 overflow-x-auto hairline-top">
            <table className="w-full min-w-[760px] border-collapse" data-testid="bankruptcy-matrix-table">
              <thead>
                <tr>
                  <th className="text-left py-4 pr-6 align-bottom border-b-2 border-navy">
                    <span className="text-[10px] uppercase tracking-[0.28em] text-slate2/60">Dimension</span>
                  </th>
                  {page.matrix.cols.map((c, i) => (
                    <th
                      key={c}
                      className={`text-left py-4 px-4 align-bottom border-b-2 border-navy ${i === 0 ? "bg-brass/[0.07]" : ""}`}
                    >
                      <div className="font-display text-base md:text-lg text-navy leading-snug max-w-[150px]">{c}</div>
                      {i === 0 && (
                        <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-oxblood">This page</div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {page.matrix.rows.map((row, r) => (
                  <tr key={r} className="border-b-0 align-top">
                    <td className="py-5 pr-6 text-xs uppercase tracking-[0.22em] text-slate2/70 font-medium">{row[0]}</td>
                    {row.slice(1).map((cell, c) => (
                      <td key={c} className={`py-5 px-4 text-sm md:text-base text-ink/85 font-cormorant leading-snug ${c === 0 ? "bg-brass/[0.05] font-medium text-navy" : ""}`}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="panel-navy py-24 md:py-32" data-testid="bankruptcy-process">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="kicker kicker-ivory">The Process</div>
          <h2 className="mt-6 font-display text-4xl md:text-5xl text-ivory leading-[1.05] max-w-3xl">{page.process.title}</h2>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
            {page.process.steps.map((s) => (
              <div key={s.num} className="hairline-top pt-6 border-transparent" data-testid={`bankruptcy-step-${s.num}`}>
                <div className="font-cormorant italic text-3xl text-brass">{s.num}</div>
                <h3 className="mt-3 font-display text-xl md:text-2xl text-ivory">{s.title}</h3>
                <p className="mt-3 text-ivory/75 font-light leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {pageFaqs.length > 0 && (
        <section className="bg-ivory py-24 md:py-32" data-testid="bankruptcy-faq">
          <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="kicker">Common Questions</div>
              <h2 className="mt-6 font-display text-4xl md:text-5xl text-navy leading-[1.05]">
                Frequently<br/>
                <span className="italic font-cormorant text-oxblood">asked.</span>
              </h2>
              <p className="mt-6 font-cormorant italic text-lg text-slate2 max-w-sm leading-snug">
                Direct answers to the questions we are asked most often about this matter.
              </p>
              <Link to="/faq" className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-oxblood hover:text-navy transition">
                All firm FAQs <ArrowRight size={14} />
              </Link>
            </div>
            <div className="lg:col-span-7 lg:col-start-6">
              {pageFaqs.map((f, i) => (
                <details key={i} className="border-b-0 py-6 group" data-testid={`bankruptcy-faq-${i}`}>
                  <summary className="cursor-pointer flex items-start gap-6 list-none">
                    <span className="font-cormorant italic text-brass text-2xl shrink-0 w-12">{String(i + 1).padStart(2, "0")}</span>
                    <span className="flex-1 font-display text-xl md:text-2xl text-ink leading-snug group-hover:text-oxblood transition">{f.q}</span>
                    <span className="shrink-0 mt-1 text-brass text-2xl group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <div className="mt-5 pl-16 pr-8 text-base md:text-lg text-slate2 font-light leading-relaxed">
                    {f.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CROSS-LINKS */}
      <section className="bg-ivory py-24" data-testid="bankruptcy-related">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          {/* Contextual authority-hub links (optional) */}
          {page.contextualLinks && (
            <div className="mb-20 pb-16 border-b border-brass/25" data-testid="bankruptcy-contextual-links">
              <div className="kicker">Related counsel</div>
              <h3 className="mt-6 font-display text-3xl md:text-4xl text-navy leading-[1.05] max-w-3xl">
                Coordinate the resolution{" "}
                <span className="italic font-cormorant text-oxblood">with the surrounding plan.</span>
              </h3>
              <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="bankruptcy-contextual-list">
                {page.contextualLinks.map((c, i) => (
                  <Link
                    key={i}
                    to={c.href}
                    className="group hairline-top pt-5 block hover:bg-linen/60 transition-colors -mx-3 px-3"
                    data-testid={`bankruptcy-contextual-${i}`}
                  >
                    <div className="text-[10px] uppercase tracking-[0.32em] text-brass font-medium">
                      {c.label}
                    </div>
                    <h4 className="mt-3 font-display text-lg md:text-xl text-navy leading-snug group-hover:text-oxblood transition-colors">
                      {c.desc}
                    </h4>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="kicker">Related Restructuring Counsel</div>
          <h3 className="mt-6 font-display text-3xl md:text-4xl text-navy leading-[1.05] max-w-3xl">
            The other paths<br/>
            <span className="italic font-cormorant text-oxblood">we counsel on.</span>
          </h3>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
            {otherPages.map((p) => (
              <Link
                key={p.slug}
                to={`/practice/${p.slug}`}
                className="block hairline-top pt-6 group"
                data-testid={`bankruptcy-cross-${p.slug}`}
              >
                <div className="text-[10px] uppercase tracking-[0.28em] text-brass">{p.kicker}</div>
                <div className="mt-3 font-display text-2xl md:text-3xl text-navy group-hover:text-oxblood transition">{p.headline.replace(/\.$/, "")}</div>
                <div className="mt-1 font-cormorant italic text-lg text-oxblood">{p.italic}</div>
                <div className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.28em] text-oxblood">
                  Read <ArrowRight size={14} />
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-16 hairline-top pt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Link to="/practice" className="group" data-testid="bankruptcy-link-practice">
              <div className="text-[10px] uppercase tracking-[0.28em] text-brass">Services</div>
              <div className="mt-2 font-display text-xl text-navy group-hover:text-oxblood transition">The Full Continuum →</div>
            </Link>
            <Link to="/about" className="group" data-testid="bankruptcy-link-firm">
              <div className="text-[10px] uppercase tracking-[0.28em] text-brass">Our Approach</div>
              <div className="mt-2 font-display text-xl text-navy group-hover:text-oxblood transition">Darryl V. Pratt — Esq., CPA →</div>
            </Link>
            <Link to="/locations" className="group" data-testid="bankruptcy-link-locations">
              <div className="text-[10px] uppercase tracking-[0.28em] text-brass">Texas Offices</div>
              <div className="mt-2 font-display text-xl text-navy group-hover:text-oxblood transition">All Five Locations →</div>
            </Link>
            <Link to="/intake" className="group" data-testid="bankruptcy-link-intake">
              <div className="text-[10px] uppercase tracking-[0.28em] text-brass">Begin</div>
              <div className="mt-2 font-display text-xl text-navy group-hover:text-oxblood transition">Request a Conference →</div>
            </Link>
          </div>

          <div className="mt-16 text-xs text-slate2/60 leading-relaxed max-w-3xl font-cormorant italic">
            This page is general information about Texas restructuring counsel and does not constitute
            legal advice. Bankruptcy is a fact-specific matter; the right path depends on circumstances
            we cannot evaluate from a public webpage. Begin with a private conference.
          </div>
        </div>
      </section>
    </div>
  );
}
